import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { X, User, FileText, Calculator, Plus, Trash, Calendar, CheckCircle, AlertTriangle, CalendarClock } from 'lucide-react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { TYPES_PRESTATIONS, calculerLignePrestationAvecCollaborateur, formaterEuro, TARIFS_REFERENCE } from '../services/tarificationService';
import { formatAddress } from '../utils/addressFormatter';
import { useClientContract } from '../hooks/useClientContract';
import InterventionScheduler from './InterventionScheduler';
import { convertToDevisLines } from '../services/complexSchedulingService';
import '../Devis.css';

const DevisModalBase = ({
  mode = 'create',
  devis = null,
  onClose,
  onSuccess,
  title,
  subtitle,
  actionLabel,
  onAction
}) => {
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [lignesPrestations, setLignesPrestations] = useState([]);
  const [contratParams, setContratParams] = useState({
    contractType: 'mandat',
    weeklyHours: 10,
    baseRateType: 'standard'
  });
  const [contratParamsSource, setContratParamsSource] = useState('manual'); // 'manual' ou 'contract'
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [rechercheClient, setRechercheClient] = useState('');
  const [periodePrestation, setPeriodePrestation] = useState({
    dateDebut: null,
    dateFin: null
  });

  // États pour la planification complexe
  const [useComplexScheduling, setUseComplexScheduling] = useState(false);
  const [showScheduler, setShowScheduler] = useState(false);
  const [employeeSchedules, setEmployeeSchedules] = useState([]);
  const [calculatedInterventions, setCalculatedInterventions] = useState(null);

  // États pour la gestion multi-prestations (Nouveau)
  const [availablePrestations, setAvailablePrestations] = useState([]);
  const [selectedPrestationId, setSelectedPrestationId] = useState(null);

  // Ref pour éviter les rechargements multiples des paramètres de contrat
  const hasLoadedContractParams = useRef(false);

  // Hook pour charger le contrat du client sélectionné
  const {
    contrat,
    loading: loadingContrat,
    aContrat,
    parametresTarifaires,
    estValidePourDevis,
    resumeContrat
  } = useClientContract(db, selectedClient?.id);

  // Fonction utilitaire pour récupérer le type de tarif depuis le devis
  const getBaseRateTypeFromDevis = (devis) => {
    // Ordre de priorité pour récupérer le type de tarif :
    // 1. Depuis les paramètres du contrat sauvegardés
    if (devis.contratParams?.baseRateType) {
      console.log('Type de tarif récupéré depuis contratParams:', devis.contratParams.baseRateType);
      return devis.contratParams.baseRateType;
    }

    // 2. Depuis la configuration des calculs
    if (devis.calculs?.configuration?.baseRateType) {
      console.log('Type de tarif récupéré depuis calculs.configuration:', devis.calculs.configuration.baseRateType);
      return devis.calculs.configuration.baseRateType;
    }

    // 3. Depuis le contrat des calculs
    if (devis.calculs?.contrat?.baseRateType) {
      console.log('Type de tarif récupéré depuis calculs.contrat:', devis.calculs.contrat.baseRateType);
      return devis.calculs.contrat.baseRateType;
    }

    // 4. Fallback : analyser le tarif de base pour déduire le type
    if (devis.calculs?.contrat?.tarifBase) {
      const tarifBase = devis.calculs.contrat.tarifBase;
      // Si le tarif est proche de 17.17€, c'est probablement exonéré 70+
      if (Math.abs(tarifBase - 17.17) < 0.1) {
        console.log('Type de tarif déduit depuis le tarif de base (17.17€): 70plus');
        return '70plus';
      }
      // Si le tarif est proche de 24.41€, c'est standard
      if (Math.abs(tarifBase - 24.41) < 0.1) {
        console.log('Type de tarif déduit depuis le tarif de base (24.41€): standard');
        return 'standard';
      }
    }

    console.warn('Impossible de récupérer le type de tarif, utilisation du défaut: standard');
    return 'standard';
  };

  // Auto-chargement des paramètres tarifaires depuis le contrat du client
  // Auto-chargement des paramètres tarifaires depuis le contrat du client
  // Auto-chargement des paramètres tarifaires depuis le contrat du client
  useEffect(() => {
    // Condition de base : on a un client et son contrat est chargé
    if (selectedClient && parametresTarifaires && aContrat) {

      // 1. Charger la liste des prestations disponibles (toujours)
      if (parametresTarifaires.prestations && parametresTarifaires.prestations.length > 0) {
        setAvailablePrestations(parametresTarifaires.prestations);
      } else {
        setAvailablePrestations([]);
      }

      // 2. Logique spécifique selon le mode
      if (!hasLoadedContractParams.current) {

        // Mode CRÉATION : On prend la valeur par défaut (première prestation ou legacy)
        if (mode === 'create') {
          if (parametresTarifaires.prestations && parametresTarifaires.prestations.length > 0) {
            const defaultPrestation = parametresTarifaires.prestations[0];
            setSelectedPrestationId(defaultPrestation.id);
            setContratParams({
              contractType: parametresTarifaires.contractType || 'mandat',
              weeklyHours: defaultPrestation.weeklyHours,
              baseRateType: defaultPrestation.baseRateType
            });
            console.log('✅ (Create) Paramètres multi-prestations chargés:', defaultPrestation);
          } else {
            // Legacy
            setContratParams({
              contractType: parametresTarifaires.contractType || 'mandat',
              weeklyHours: parametresTarifaires.weeklyHours || 10,
              baseRateType: parametresTarifaires.baseRateType || 'standard'
            });
            console.log('✅ (Create) Paramètres tarifaires standard chargés:', parametresTarifaires);
          }
        }

        // Mode ÉDITION : On essaie de retrouver la prestation correspondante
        else if (mode === 'edit' && devis) {
          // On a déjà chargé les params du devis dans l'autre useEffect (celui qui dépend de [mode, devis])
          // Ici on essaie juste de "re-lier" ces params à une prestation existante pour que le dropdown soit correct
          if (parametresTarifaires.prestations && parametresTarifaires.prestations.length > 0) {
            // Essayer de trouver une prestation qui matche le type et les heures du devis
            // Note: Le 'type' exact n'est pas stocké dans contratParams du devis, mais on peut deviner via baseRateType + weeklyHours,
            // ou mieux, si on a sauvegardé l'ID de prestation (ce qui n'est pas le cas actuellement).
            // On va essayer de matcher par weeklyHours et baseRateType.
            const prestationMatch = parametresTarifaires.prestations.find(p =>
              p.weeklyHours === contratParams.weeklyHours &&
              p.baseRateType === contratParams.baseRateType
            );

            if (prestationMatch) {
              setSelectedPrestationId(prestationMatch.id);
              console.log('✅ (Edit) Prestation correspondante retrouvée:', prestationMatch);
            } else {
              // Si pas de match exact, on ne sélectionne rien (mode personnalisé) ou le premier ?
              // On laisse null pour indiquer "Personnalisé" ou autre, ou on force le premier ?
              // Mieux vaut ne rien forcer si ça ne matche pas, pour ne pas écraser les données du devis.
              console.log('⚠️ (Edit) Pas de prestation correspondante exacte trouvée.');
            }
          }
        }

        setContratParamsSource('contract');
        hasLoadedContractParams.current = true;
      }
    }
  }, [selectedClient, parametresTarifaires, aContrat, mode, devis, contratParams]);

  // Handler pour le changement de prestation sélectionnée
  const handlePrestationChange = (prestationId) => {
    const prestation = availablePrestations.find(p => p.id === prestationId);
    if (prestation) {
      setSelectedPrestationId(prestation.id);
      setContratParams(prev => ({
        ...prev,
        weeklyHours: prestation.weeklyHours,
        baseRateType: prestation.baseRateType
      }));

      // Mettre à jour automatiquement les types des lignes existantes ou par défaut
      // Si on a une seule ligne par défaut (pas encore modifié), on change son type
      if (lignesPrestations.length === 1 && lignesPrestations[0].quantite === 1) {
        setLignesPrestations(prev => [{
          ...prev[0],
          typePrestation: prestation.type // 'menage', 'assistance_vie', etc.
        }]);
      }
    }
  };

  // Réinitialiser le flag quand le client change
  useEffect(() => {
    hasLoadedContractParams.current = false;
  }, [selectedClient]);

  // Initialisation des données
  useEffect(() => {
    if (mode === 'edit' && devis) {
      setSelectedClient({
        id: devis.clientId,
        firstName: devis.clientNom?.split(' ')[0] || '',
        lastName: devis.clientNom?.split(' ').slice(1).join(' ') || '',
        email: devis.clientEmail || '',
        phoneNumber: devis.clientTelephone || '',
        address: devis.clientAdresse || ''
      });

      if (devis.lignesPrestations) {
        setLignesPrestations(devis.lignesPrestations);
      }

      // Récupération améliorée des paramètres du contrat
      const baseRateType = getBaseRateTypeFromDevis(devis);
      const contractType = devis.contratParams?.contractType ||
        devis.calculs?.contrat?.type ||
        'mandat';
      const weeklyHours = devis.contratParams?.weeklyHours ||
        devis.calculs?.contrat?.volumeHebdo ||
        10;

      console.log('Paramètres du contrat récupérés:', {
        contractType,
        weeklyHours,
        baseRateType
      });

      setContratParams({
        contractType,
        weeklyHours,
        baseRateType
      });

      // Initialiser les dates de période
      if (devis.dateDebut || devis.dateFin) {
        setPeriodePrestation({
          dateDebut: devis.dateDebut ? (typeof devis.dateDebut.toDate === 'function' ? devis.dateDebut.toDate() : new Date(devis.dateDebut)) : null,
          dateFin: devis.dateFin ? (typeof devis.dateFin.toDate === 'function' ? devis.dateFin.toDate() : new Date(devis.dateFin)) : null
        });
      }
    } else if (mode === 'create') {
      setLignesPrestations([{
        id: 1,
        typePrestation: 'aide_domicile',
        quantite: 1,
        majoration: 'none',
        distanceAllerKm: 0,
        nbInterventions: 1,
        employeeId: null
      }]);
    }
  }, [mode, devis]);

  // Chargement des données
  useEffect(() => {
    const loadData = async () => {
      try {
        // Charger clients
        const clientsSnapshot = await getDocs(collection(db, 'clients'));
        const allClients = clientsSnapshot.docs.map(doc => {
          const data = doc.data();
          const type = data.typeEntite === 'prospect' ? 'prospect' : 'client';

          return {
            id: doc.id,
            ...data,
            type,
            firstName: data.firstName || data.prenom || '',
            lastName: data.lastName || data.nom || '',
            email: data.email || '',
            phoneNumber: data.phoneNumber || data.telephone || '',
            address: data.address || data.adresse || ''
          };
        });

        const clientsActifs = allClients.filter(client =>
          client.type === 'prospect' ||
          (client.isActive !== false &&
            client.statutClient !== 'inactif' &&
            client.statutClient !== 'suspendu')
        );

        setClients(clientsActifs);

        // Charger employés
        const employeesSnapshot = await getDocs(collection(db, 'employees'));
        const employeesList = employeesSnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(employee => {
            // Exclure les bénéficiaires (rôle 'beneficiaire')
            if (employee.role === 'beneficiaire') {
              return false;
            }
            // Exclure les employés archivés (inclut ceux sans le champ isArchived)
            if (employee.isArchived === true) {
              return false;
            }
            return true;
          });
        setEmployees(employeesList);
      } catch (error) {
        console.error('Erreur chargement données:', error);
      }
    };

    loadData();
  }, []);

  // Calcul de l'aperçu
  useEffect(() => {
    if (selectedClient && lignesPrestations.length > 0) {
      setCalculating(true);
      setTimeout(() => {
        let totalCesu = 0, totalMandat = 0, totalDeplacement = 0, totalHeures = 0;

        const lignesCalculees = lignesPrestations.map(ligne => {
          try {
            const calcul = calculerLignePrestationAvecCollaborateur(ligne, contratParams, employees);
            totalCesu += calcul.totalCesu;
            totalMandat += calcul.totalMandat;
            totalDeplacement += calcul.totalDeplacement;
            totalHeures += calcul.heuresTotal;
            return calcul;
          } catch (err) {
            console.warn('Erreur calcul ligne:', err);
            return null;
          }
        }).filter(Boolean);

        setPreview({
          lignes: lignesCalculees,
          totaux: { totalCesu, totalMandat, totalDeplacement, totalHeures, totalTTC: totalCesu + totalMandat + totalDeplacement }
        });
        setCalculating(false);
      }, 300);
    } else {
      setPreview(null);
    }
  }, [selectedClient, lignesPrestations, contratParams, employees]);

  // Gestion des lignes - Utilisez useCallback pour éviter les re-renders
  const ajouterLigne = useCallback(() => {
    const nouvelleId = Math.max(...lignesPrestations.map(l => l.id), 0) + 1;
    setLignesPrestations(prev => [...prev, {
      id: nouvelleId,
      typePrestation: 'aide_domicile',
      quantite: 1,
      majoration: 'none',
      distanceAllerKm: 0,
      nbInterventions: 1,
      employeeId: null
    }]);
  }, [lignesPrestations]);

  const supprimerLigne = useCallback((id) => {
    if (lignesPrestations.length > 1) {
      setLignesPrestations(prev => prev.filter(l => l.id !== id));
    }
  }, [lignesPrestations.length]);

  const updateLigne = useCallback((id, field, value) => {
    setLignesPrestations(prev => prev.map(ligne =>
      ligne.id === id ? { ...ligne, [field]: value } : ligne
    ));
  }, []);

  // Gestion bénéficiaire
  const handleSelectClient = useCallback((client) => {
    setSelectedClient(client);
    setRechercheClient('');
  }, []);

  const handleResetClient = useCallback(() => {
    setSelectedClient(null);
  }, []);

  // Gestion planification complexe
  const handleSaveScheduling = useCallback((schedules, interventions) => {
    console.log('💾 Sauvegarde du planning:', schedules, interventions);
    setEmployeeSchedules(schedules);
    setCalculatedInterventions(interventions);

    // Générer automatiquement les lignes de devis
    const generatedLines = convertToDevisLines(interventions, contratParams);
    setLignesPrestations(generatedLines);

    setShowScheduler(false);
  }, [contratParams]);

  const handleCancelScheduling = useCallback(() => {
    setShowScheduler(false);
  }, []);

  // Désactiver la planification complexe
  const handleDisableComplexScheduling = useCallback(() => {
    setUseComplexScheduling(false);
    setEmployeeSchedules([]);
    setCalculatedInterventions(null);
    // Revenir à une ligne simple
    setLignesPrestations([{
      id: 1,
      typePrestation: 'aide_domicile',
      quantite: 1,
      majoration: 'none',
      distanceAllerKm: 0,
      nbInterventions: 1,
      employeeId: null
    }]);
  }, []);

  // Composants réutilisables - Utilisez useMemo pour éviter les re-renders
  const ClientSelector = useMemo(() => (
    <div className="devis-section">
      <h3 className="devis-section-title"><User size={18} />1. Sélection du bénéficiaire</h3>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Sélection rapide</label>
          <select
            value={selectedClient?.id || ''}
            onChange={(e) => {
              const clientId = e.target.value;
              if (clientId) {
                const client = clients.find(c => c.id === clientId);
                handleSelectClient(client);
              } else {
                handleResetClient();
              }
            }}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="">Choisir un bénéficiaire...</option>
            {clients.map(client => (
              <option key={client.id} value={client.id}>
                {client.lastName} {client.firstName} {client.type === 'prospect' ? ' (Prospect)' : ''}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Recherche avancée</label>
          <div className="relative">
            <input
              type="text"
              placeholder="Rechercher par nom, prénom, email..."
              value={rechercheClient}
              onChange={(e) => setRechercheClient(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            {rechercheClient && (
              <button
                onClick={() => setRechercheClient('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>
        </div>
      </div>

      {rechercheClient.trim() && (
        <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg mb-3">
          {clients
            .filter(client => {
              const recherche = rechercheClient.toLowerCase();
              const nomComplet = `${client.lastName} ${client.firstName}`.toLowerCase();
              const email = client.email?.toLowerCase() || '';
              return nomComplet.includes(recherche) || email.includes(recherche) ||
                client.lastName?.toLowerCase().includes(recherche) || client.firstName?.toLowerCase().includes(recherche);
            })
            .map(client => (
              <div
                key={client.id}
                onClick={() => handleSelectClient(client)}
                className={`p-3 border-b border-gray-100 cursor-pointer hover:bg-blue-50 transition-colors ${selectedClient?.id === client.id ? 'bg-blue-100 border-blue-200' : ''
                  }`}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-medium">
                      {client.type === 'prospect' && (
                        <span className="inline-block bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full mr-2">PROSPECT</span>
                      )}
                      {client.lastName} {client.firstName}
                    </div>
                    <div className="text-sm text-gray-600">{client.email}</div>
                    {client.phoneNumber && <div className="text-xs text-gray-500">{client.phoneNumber}</div>}
                  </div>
                  {selectedClient?.id === client.id && <div className="text-green-600">✓</div>}
                </div>
              </div>
            ))
          }

          {clients.filter(client => {
            const recherche = rechercheClient.toLowerCase();
            const nomComplet = `${client.lastName} ${client.firstName}`.toLowerCase();
            const email = client.email?.toLowerCase() || '';
            return nomComplet.includes(recherche) || email.includes(recherche) ||
              client.lastName?.toLowerCase().includes(recherche) || client.firstName?.toLowerCase().includes(recherche);
          }).length === 0 && (
              <div className="p-4 text-center text-gray-500">Aucun bénéficiaire ou prospect trouvé pour "{rechercheClient}"</div>
            )}
        </div>
      )}

      {selectedClient && (
        <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="font-medium text-green-800">Bénéficiaire sélectionné: {selectedClient.lastName} {selectedClient.firstName}</div>
          <div className="text-sm text-green-600">{selectedClient.email} • {selectedClient.type === 'prospect' ? 'Prospect' : 'Bénéficiaire'}</div>
          <div className="text-xs text-green-500 mt-1">
            {selectedClient.phoneNumber && `Tél: ${selectedClient.phoneNumber} • `}
            {selectedClient.address && `Adresse: ${formatAddress(selectedClient.address)}`}
          </div>
        </div>
      )}
    </div>
  ), [clients, selectedClient, rechercheClient, handleSelectClient, handleResetClient]);

  const ContractParams = useMemo(() => (
    <div className="devis-section">
      <h3 className="devis-section-title"><FileText size={18} />2. Paramètres tarifaires</h3>

      {/* Indicateur de source des paramètres */}
      {selectedClient && (
        <div className="mb-3">
          {aContrat && contratParamsSource === 'contract' ? (
            <div className="flex items-center space-x-2 p-2 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle size={16} className="text-green-600 flex-shrink-0" />
              <div className="text-sm text-green-800 flex-1">
                <strong>Paramètres chargés depuis le contrat</strong>
                {resumeContrat && (
                  <span className="text-green-600 ml-2">
                    ({resumeContrat.type} • {resumeContrat.volumeHebdo}h/sem • Tarif {resumeContrat.tarifBase})
                  </span>
                )}
              </div>
            </div>
          ) : !loadingContrat && !aContrat && (
            <div className="flex items-center space-x-2 p-2 bg-orange-50 border border-orange-200 rounded-lg">
              <AlertTriangle size={16} className="text-orange-600 flex-shrink-0" />
              <div className="text-sm text-orange-800">
                <strong>Aucun contrat trouvé</strong>
                <span className="text-orange-600 ml-2">
                  • Créez un contrat via "Gérer contrats" pour auto-remplir ces paramètres
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="devis-grid devis-grid-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Type de contrat</label>
          <select
            value={contratParams.contractType}
            onChange={(e) => setContratParams({ ...contratParams, contractType: e.target.value })}
            className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
          >
            <option value="mandat">Mandat</option>
            <option value="prestation">Prestation</option>
          </select>
        </div>

        {/* Sélecteur de prestation si disponible */}
        {availablePrestations.length > 0 && (
          <div className="col-span-1 md:col-span-2 lg:col-span-3 bg-blue-50 p-3 rounded-lg border border-blue-200 mb-4 order-last lg:order-none">
            <label className="block text-sm font-bold text-blue-800 mb-2">Prestation à facturer (Multi-Contrats)</label>
            <select
              value={selectedPrestationId || ''}
              onChange={(e) => handlePrestationChange(e.target.value)}
              className="w-full p-2 border border-blue-300 rounded focus:ring-2 focus:ring-blue-500 text-blue-900 font-medium"
            >
              {availablePrestations.map(p => {
                const label = TARIFS_REFERENCE[p.type]?.label || p.type;
                return (
                  <option key={p.id} value={p.id}>
                    {label} — {p.weeklyHours}h/sem — Tarif {p.baseRateType === '70plus' ? 'Exonéré' : 'Standard'}
                  </option>
                );
              })}
            </select>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Volume hebdomadaire (heures)</label>
          <input
            type="number"
            min="1"
            max="60"
            value={contratParams.weeklyHours}
            onChange={(e) => setContratParams({ ...contratParams, weeklyHours: parseInt(e.target.value) })}
            className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
            disabled={availablePrestations.length > 0} // Désactivé si piloté par la prestation
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Type de tarif</label>
          <select
            value={contratParams.baseRateType}
            onChange={(e) => setContratParams({ ...contratParams, baseRateType: e.target.value })}
            className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
            disabled={availablePrestations.length > 0} // Désactivé si piloté par la prestation
          >
            {(() => {
              // Récupérer le tarif spécifique si une prestation est sélectionnée
              let tarifStandard = 24.41;
              let tarifExonere = 17.17;

              if (selectedPrestationId) {
                const prestation = availablePrestations.find(p => p.id === selectedPrestationId);
                if (prestation) {
                  const tarifsRef = TARIFS_REFERENCE[prestation.type];
                  if (tarifsRef) {
                    tarifStandard = tarifsRef.standard;
                    tarifExonere = tarifsRef.plus70;
                  }
                }
              }

              return (
                <>
                  <option value="standard">Standard ({tarifStandard.toFixed(2).replace('.', ',')}€/h)</option>
                  <option value="70plus">Exonération 70+ ({tarifExonere.toFixed(2).replace('.', ',')}€/h)</option>
                </>
              );
            })()}
          </select>
        </div>
      </div>
    </div>
  ), [contratParams, setContratParams, selectedClient, aContrat, contratParamsSource, loadingContrat, resumeContrat, availablePrestations, selectedPrestationId]);

  // Période de prestation
  const PeriodePrestationSection = useMemo(() => (
    <div className="devis-section">
      <h3 className="devis-section-title">
        <Calendar size={18} />
        3. Période de prestation (optionnel)
      </h3>
      <div className="devis-grid devis-grid-2">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Date de début</label>
          <input
            type="date"
            value={periodePrestation.dateDebut
              ? periodePrestation.dateDebut.toISOString().split('T')[0]
              : ''}
            onChange={(e) => setPeriodePrestation({
              ...periodePrestation,
              dateDebut: e.target.value ? new Date(e.target.value) : null
            })}
            className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Date de fin</label>
          <input
            type="date"
            value={periodePrestation.dateFin
              ? periodePrestation.dateFin.toISOString().split('T')[0]
              : ''}
            onChange={(e) => setPeriodePrestation({
              ...periodePrestation,
              dateFin: e.target.value ? new Date(e.target.value) : null
            })}
            className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>
      {periodePrestation.dateDebut && periodePrestation.dateFin && (
        <div className="mt-2 text-sm text-gray-600">
          Durée : {Math.ceil((periodePrestation.dateFin - periodePrestation.dateDebut) / (1000 * 60 * 60 * 24)) + 1} jours
        </div>
      )}
    </div>
  ), [periodePrestation]);

  // Section planification complexe
  const ComplexSchedulingSection = useMemo(() => (
    <div className="devis-section">
      <h3 className="devis-section-title">
        <CalendarClock size={18} />
        4. Planification des interventions (optionnel)
      </h3>

      <div className="mb-3">
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={useComplexScheduling}
            onChange={(e) => {
              if (e.target.checked) {
                setUseComplexScheduling(true);
              } else {
                handleDisableComplexScheduling();
              }
            }}
            className="rounded border-gray-300"
          />
          <span className="text-sm font-medium text-gray-700">
            Utiliser la planification avancée multi-intervenants
          </span>
        </label>
        <p className="text-xs text-gray-500 mt-1 ml-6">
          Permet de définir des jours fixes et une alternance semaine A/B par intervenant avec détection automatique des majorations
        </p>
      </div>

      {useComplexScheduling && (
        <div>
          <button
            onClick={() => setShowScheduler(true)}
            disabled={!periodePrestation.dateDebut || !periodePrestation.dateFin}
            className="devis-btn devis-btn-primary mb-3"
            title={!periodePrestation.dateDebut || !periodePrestation.dateFin ? "Définissez d'abord une période de prestation" : ""}
          >
            <CalendarClock size={16} />
            {employeeSchedules.length > 0 ? 'Modifier le planning' : 'Configurer le planning'}
          </button>

          {employeeSchedules.length > 0 && calculatedInterventions && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="font-medium text-blue-800 mb-2">
                ✓ Planning configuré pour {employeeSchedules.length} intervenant(s)
              </div>
              <div className="text-sm text-blue-600 space-y-1">
                {Object.entries(calculatedInterventions).map(([empId, data]) => (
                  <div key={empId}>
                    • {data.employeeName} : {data.total.interventions} interventions ({data.total.hours.toFixed(1)}h)
                    {data.weekend.interventions > 0 && <span className="ml-2 text-orange-600">dont {data.weekend.interventions} passage{data.weekend.interventions > 1 ? 's' : ''} le dimanche</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  ), [useComplexScheduling, employeeSchedules, calculatedInterventions, periodePrestation, handleDisableComplexScheduling]);

  const PrestationLines = useMemo(() => (
    <div className="devis-section">
      <div className="flex justify-between items-center mb-3">
        <h3 className="devis-section-title"><Calculator size={18} />5. Prestations</h3>
        <button onClick={ajouterLigne} className="devis-btn devis-btn-primary">
          <Plus size={16} />Ajouter
        </button>
      </div>

      <div className="space-y-3">
        {lignesPrestations.map((ligne) => (
          <div key={ligne.id} className="devis-ligne-prestation">
            <div className="devis-ligne-grid">
              <div>
                <label className="devis-ligne-label">Prestation</label>
                <select
                  value={ligne.typePrestation}
                  onChange={(e) => updateLigne(ligne.id, 'typePrestation', e.target.value)}
                  className="devis-ligne-input"
                >
                  {Object.entries(TYPES_PRESTATIONS).map(([key, value]) => (
                    <option key={key} value={key}>{value.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="devis-ligne-label">Quantité</label>
                <input
                  type="number"
                  min="0.5"
                  step="0.5"
                  value={ligne.quantite}
                  onChange={(e) => updateLigne(ligne.id, 'quantite', parseFloat(e.target.value) || 0)}
                  className="devis-ligne-input"
                />
              </div>
              <div>
                <label className="devis-ligne-label">Intervenant</label>
                <select
                  value={ligne.employeeId || ''}
                  onChange={(e) => updateLigne(ligne.id, 'employeeId', e.target.value || null)}
                  className="devis-ligne-input"
                >
                  <option value="">Sélectionner...</option>
                  {employees.map(employee => (
                    <option key={employee.id} value={employee.id}>
                      {employee.firstName} {employee.lastName}
                      {employee.professionalInfo?.cesuNumber ? ` • CESU: ${employee.professionalInfo.cesuNumber}` : ''}
                      {employee.professionalInfo?.nanCrcesu ? ` • NAN: ${employee.professionalInfo.nanCrcesu}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="devis-ligne-label">Majoration</label>
                <select
                  value={ligne.majoration}
                  onChange={(e) => updateLigne(ligne.id, 'majoration', e.target.value)}
                  className="devis-ligne-input"
                >
                  <option value="none">Aucune</option>
                  <option value="weekend">Dimanche (+25%)</option>
                  <option value="holiday">Jour férié (+50%)</option>
                  <option value="may1">1er mai (+100%)</option>
                </select>
              </div>
              <div>
                <label className="devis-ligne-label">Distance (km)</label>
                <input
                  type="number"
                  min="0"
                  value={ligne.distanceAllerKm}
                  onChange={(e) => updateLigne(ligne.id, 'distanceAllerKm', parseFloat(e.target.value) || 0)}
                  className="devis-ligne-input"
                />
              </div>
              <div>
                <label className="devis-ligne-label">Interventions</label>
                <input
                  type="number"
                  min="1"
                  value={ligne.nbInterventions}
                  onChange={(e) => updateLigne(ligne.id, 'nbInterventions', parseInt(e.target.value) || 1)}
                  className="devis-ligne-input"
                />
              </div>
              <div className="devis-ligne-actions">
                {lignesPrestations.length > 1 && (
                  <button
                    onClick={() => supprimerLigne(ligne.id)}
                    className="devis-btn-remove"
                    title="Supprimer cette ligne"
                  >
                    <Trash size={16} />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  ), [lignesPrestations, employees, ajouterLigne, supprimerLigne, updateLigne]);

  // Composant pour l'aperçu des calculs
  const Preview = useMemo(() => (
    <div className="devis-preview">
      <h3 className="devis-section-title"><Calculator size={18} />6. Aperçu des calculs</h3>

      {calculating ? (
        <div className="devis-preview-loading">
          <div className="devis-preview-spinner"></div>
          <p>Calcul en cours...</p>
        </div>
      ) : preview ? (
        <div className="space-y-3">
          {/* Détail par ligne */}
          <div className="devis-preview-detail">
            <h4 className="font-medium mb-2">Détail des prestations</h4>
            {preview.lignes.map((ligne, index) => {
              const ligneOriginale = lignesPrestations[index];
              const collaborateur = ligneOriginale?.employeeId
                ? employees.find(e => e.id === ligneOriginale.employeeId)
                : null;

              return (
                <div key={index} className="devis-total-line">
                  <div className="flex flex-col">
                    <span>{ligne.typePrestation} x {ligneOriginale?.quantite}</span>
                    {collaborateur && (
                      <span className="text-xs text-gray-500">
                        Intervenant: {collaborateur.firstName} {collaborateur.lastName}
                        {collaborateur.professionalInfo?.cesuNumber && (
                          <span className="ml-2">• CESU: {collaborateur.professionalInfo.cesuNumber}</span>
                        )}
                        {collaborateur.professionalInfo?.nanCrcesu && (
                          <span className="ml-2">• NAN: {collaborateur.professionalInfo.nanCrcesu}</span>
                        )}
                      </span>
                    )}
                  </div>
                  <span>{formaterEuro(ligne.totalLigne)}</span>
                </div>
              );
            })}
          </div>

          {/* Totaux */}
          <div className="devis-preview-totaux">
            <h4 className="font-medium mb-2">Récapitulatif</h4>
            <div className="space-y-1 text-sm">
              <div className="devis-total-line">
                <span>Part CESU (salaire + charges):</span>
                <span>{formaterEuro(preview.totaux.totalCesu)}</span>
              </div>
              <div className="devis-total-line">
                <span>Frais mandat OMAD:</span>
                <span>{formaterEuro(preview.totaux.totalMandat)}</span>
              </div>

              {preview.totaux.totalDeplacement > 0 && (
                <>
                  <div className="border-t pt-1 mt-1">
                    <div className="devis-total-line font-medium">
                      <span>Frais de déplacement:</span>
                      <span>{formaterEuro(preview.totaux.totalDeplacement)}</span>
                    </div>

                    {preview.lignes
                      .filter(ligne => ligne.totalDeplacement > 0)
                      .map((ligne, index) => {
                        const ligneOriginale = lignesPrestations[index];
                        const collaborateur = ligneOriginale?.employeeId
                          ? employees.find(e => e.id === ligneOriginale.employeeId)
                          : null;

                        return (
                          <div key={index} className="devis-total-line text-xs text-gray-600 ml-2">
                            <span>
                              {collaborateur
                                ? `${collaborateur.firstName} ${collaborateur.lastName}`
                                : 'Intervenant standard'
                              }
                              {collaborateur?.professionalInfo?.cesuNumber && (
                                <span className="ml-1">• CESU: {collaborateur.professionalInfo.cesuNumber}</span>
                              )}
                              {collaborateur?.professionalInfo?.nanCrcesu && (
                                <span className="ml-1">• NAN: {collaborateur.professionalInfo.nanCrcesu}</span>
                              )}:
                            </span>
                            <span>{formaterEuro(ligne.totalDeplacement)}</span>
                          </div>
                        );
                      })}
                  </div>
                </>
              )}

              <div className="devis-total-final">
                <span>Total TTC:</span>
                <span className="devis-text-success">{formaterEuro(preview.totaux.totalTTC)}</span>
              </div>
              <div className="devis-total-line text-xs text-gray-600">
                <span>Coût après crédit d'impôt 50%:</span>
                <span>{formaterEuro(preview.totaux.totalTTC / 2)}</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <p className="text-gray-600">Sélectionnez un bénéficiaire pour voir l'aperçu des calculs</p>
      )}
    </div>
  ), [calculating, preview, lignesPrestations, employees]);

  // Actions
  const Actions = useMemo(() => (
    <div className="devis-actions">
      <button
        onClick={onClose}
        className="devis-btn devis-btn-secondary"
        disabled={loading}
      >
        Annuler
      </button>
      <button
        onClick={() => onAction && onAction({
          selectedClient,
          lignesPrestations,
          contratParams,
          periodePrestation,
          preview
        })}
        disabled={loading || !selectedClient || !preview}
        className="devis-btn devis-btn-primary"
      >
        {loading ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            {actionLabel || 'En cours...'}
          </>
        ) : (
          <>
            <FileText size={16} />
            {actionLabel || 'Valider'}
          </>
        )}
      </button>
    </div>
  ), [onClose, loading, selectedClient, preview, onAction, lignesPrestations, contratParams, periodePrestation, actionLabel]);

  return (
    <div className="devis-modal-container">
      <div className="devis-modal-content devis-fade-in">
        {/* En-tête */}
        <div className="devis-modal-header">
          <div>
            <h2 className="devis-modal-title">{title || (mode === 'create' ? 'Créer un nouveau devis' : 'Modifier le devis')}</h2>
            <p className="devis-modal-subtitle">{subtitle || (mode === 'create' ? 'Configurez les prestations et générez un devis avec les tarifs OMAD' : 'Modifiez les prestations et les paramètres tarifaires')}</p>
          </div>
          <button onClick={onClose} className="devis-modal-close">
            <X size={24} />
          </button>
        </div>

        <div className="devis-modal-body">
          {ClientSelector}
          {ContractParams}
          {PeriodePrestationSection}
          {ComplexSchedulingSection}
          {!useComplexScheduling && PrestationLines}
          {selectedClient && Preview}
          {Actions}
        </div>

        {/* Modal InterventionScheduler */}
        {showScheduler && (
          <InterventionScheduler
            employees={employees}
            periodePrestation={periodePrestation}
            weeklyHoursTarget={contratParams.weeklyHours}
            onSave={handleSaveScheduling}
            onCancel={handleCancelScheduling}
            initialSchedules={employeeSchedules}
          />
        )}
      </div>
    </div>
  );
};

export default DevisModalBase;
